import React, { type ReactNode } from "react";
import { Plus } from "lucide-react";
import { formatDaysText, getRemainingDaysUntil } from "@intelligems/ig-utils";
import { IgButton, IgChip, IgTypography } from "@intelligems/ig-theme";
import { Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { CreateExperienceDialog } from "@src/components/Dialogs/CreateExperienceDialog/CreateExperienceDialog";
import { type CategoryKey } from "@src/components/Dialogs/CreateExperienceDialog/types";
import { MODULE_ID_BY_EXPERIENCE_MODULE } from "@src/constants/billing";
import { Resources } from "@src/components/MainApp/Utils/Resources";
import { StartFromScratchCards } from "@src/features/offers/components/StartFromScratchCards";
import { useModuleTrials } from "@src/features/sharedExperience/hooks/use-module-trials";
import { useDialogs } from "@src/hooks/dialog";
import { getTrialUrgencyColor } from "@src/utils/trial-deadline";

const TRIAL_BANNER_MODULE_LABEL: Partial<Record<CategoryKey, string>> = {
  merchandising: "Smart Merchandising",
  pricing: "Smart Pricing",
  shipping: "Smart Shipping",
  postPurchase: "Smart Post-purchase",
  checkout: "Smart Checkout",
};

type LegacyRenderLocation =
  | "noOffers"
  | "noExperiments"
  | "noCheckout"
  | "noPostPurchase";

interface ModuleEmptyStateCopy {
  heading: string;
  description: string;
  buttonLabel: string;
}

export const MODULE_EMPTY_STATE_COPY = {
  content: {
    heading: "Discover content that converts",
    description:
      "Run A/B tests and personalize your storefront content to match what actually converts.",
    buttonLabel: "New Content Experience",
  },
  merchandising: {
    heading: "Turn browsers into bigger buyers",
    description:
      "Test bundle offers, progress bars, and more to drive higher cart values before checkout.",
    buttonLabel: "New Merchandising Experience",
  },
  pricing: {
    heading: "Upgrade to find your perfect price point",
    description:
      "A/B test prices in real time to discover the sweet spot that maximizes profit, revenue, and conversion.",
    buttonLabel: "New Pricing Experience",
  },
  shipping: {
    heading: "Optimize every shipping decision",
    description:
      "Test and personalize shipping rates, free shipping thresholds, and offers to reduce cart abandonment.",
    buttonLabel: "New Shipping Experience",
  },
  checkout: {
    heading: "Make more money at checkout",
    description:
      "Test and personalize on recommended products, offers, and trust signals to convert more at checkout.",
    buttonLabel: "New Checkout Experience",
  },
  postPurchase: {
    heading: "Make more money on every order",
    description:
      "A/B test and personalize post-purchase offers, recommended products, and one-click add.",
    buttonLabel: "New Post-purchase Experience",
  },
} satisfies Record<CategoryKey, ModuleEmptyStateCopy>;

interface LegacyNoExperiencesProps {
  heading: string;
  description: string;
  renderLocation: LegacyRenderLocation;
  showResources?: boolean;
  gradientWord?: string;
  module?: never;
  graphic?: never;
}

interface ModuleNoExperiencesProps {
  module: CategoryKey;
  graphic: ReactNode;
  heading?: never;
  description?: never;
  renderLocation?: never;
  showResources?: never;
  gradientWord?: never;
}

type NoExperiencesProps = LegacyNoExperiencesProps | ModuleNoExperiencesProps;

const ModuleNoExperiences = ({ graphic, module }: ModuleNoExperiencesProps) => {
  const theme = useTheme();
  const { openDialog } = useDialogs();
  const copy = MODULE_EMPTY_STATE_COPY[module];
  const trialModuleLabel = TRIAL_BANNER_MODULE_LABEL[module];
  const moduleTrials = useModuleTrials();
  const moduleId = MODULE_ID_BY_EXPERIENCE_MODULE[module];
  const trialEndAt = trialModuleLabel
    ? moduleTrials.find((trial) => trial.moduleId === moduleId)?.endDate
    : undefined;
  const trialDaysLeft =
    trialEndAt !== undefined ? getRemainingDaysUntil(trialEndAt) : undefined;

  return (
    <Stack>
      <CreateExperienceDialog renderLocation={module} />
      <Stack alignItems="center">
        <Stack
          sx={{
            width: "100%",
            height: 500,
            overflow: "hidden",
          }}
        >
          {graphic}
        </Stack>
        <Stack alignItems="center" spacing={0} sx={{ maxWidth: 650 }}>
          {trialDaysLeft !== undefined && (
            <IgChip
              color={getTrialUrgencyColor(trialDaysLeft)}
              size="small"
              label={`${formatDaysText(trialDaysLeft)} left in trial`}
              sx={{
                mb: 4,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            />
          )}
          <IgTypography
            variant="h2"
            fontWeight={600}
            sx={{
              textAlign: "center",
            }}
          >
            {trialDaysLeft !== undefined
              ? `You've unlocked 14 days of ${trialModuleLabel}!`
              : copy.heading}
          </IgTypography>
          <IgTypography
            sx={{
              color: theme.palette.text.secondary,
              mt: 3,
              textAlign: "center",
            }}
          >
            {copy.description}
          </IgTypography>
          <IgButton
            onClick={() => openDialog("createExperiment")}
            startIcon={<Plus color={theme.igIconColors.dark} size={16} />}
            sx={{
              mt: 7,
            }}
            variant="contained"
          >
            {copy.buttonLabel}
          </IgButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const NoExperiences = (props: NoExperiencesProps) => {
  const theme = useTheme();

  if (props.module) {
    return (
      <ModuleNoExperiences graphic={props.graphic} module={props.module} />
    );
  }

  const {
    heading,
    description,
    renderLocation,
    showResources,
    gradientWord = "Build",
  } = props;

  return (
    <Grid container direction="column" alignItems="center" spacing={16} mb={4}>
      <Grid container justifyContent="center" spacing={6} maxWidth="800px">
        <Grid
          container
          justifyContent="center"
          direction="column"
          alignItems="center"
        >
          <Grid container direction="column" width={"500px"} spacing={4}>
            <Grid textAlign={"center"}>
              <IgTypography variant={"display"} fontWeight={600}>
                <span
                  style={{
                    // eslint-disable-next-line ig-fe-requirements/no-hardcoded-hex-colors -- no close ig-theme match
                    background: `linear-gradient(141.34deg, ${theme.igPrimitives.base[900]} 8.89%, ${theme.igPrimitives.base[500]} 31.67%, ${theme.igPrimitives.teal[400]} 77.22%, #00C851 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {gradientWord}
                </span>{" "}
                {heading}
              </IgTypography>
            </Grid>
            <Grid textAlign={"center"}>
              <IgTypography>{description}</IgTypography>
            </Grid>
          </Grid>
          <Grid width="800px">
            <StartFromScratchCards
              renderLocation={renderLocation}
              wrap={true}
              hideTitle={true}
            />
          </Grid>
          {renderLocation === "noOffers" && (
            <Grid
              width="800px"
              textAlign={"center"}
              mt={16}
              id={"ig-resources-to-get-started"}
            >
              <IgTypography variant={"h2"} fontWeight={600}>
                Resources to help you get started
              </IgTypography>
            </Grid>
          )}
        </Grid>
      </Grid>
      {showResources && (
        <Grid sx={{ width: "920px" }}>
          <Resources isStandardWidth={true} />
        </Grid>
      )}
    </Grid>
  );
};
